import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
  FormControlLabel, Checkbox, TextField, Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import useAtsSenderName from '../../hooks/useAtsSenderName';

// Resuelve solo {{cargo}} y {{empresa}}. {{candidato}} se deja sin resolver:
// lo completa el backend con el nombre de cada destinatario.
const resolveNonCandidate = (text, { cargo, empresa }) =>
  String(text || '')
    .replace(/\{\{\s*cargo\s*\}\}/g, cargo || '')
    .replace(/\{\{\s*empresa\s*\}\}/g, empresa || '');

/**
 * Diálogo de descarte masivo. El correo no es personalizable por candidato:
 * se muestra el asunto/cuerpo (solo lectura) y, si se confirma el envío, se
 * manda el mismo texto a todos (con el nombre de cada uno completado en el back).
 */
const AtsBulkDiscardDialog = ({
  open,
  onClose,
  count = 0,
  rejectionTemplate = { subject: '', body: '' },
  positionName = '',
  orgName = '',
  autoSendDefault = false,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [sendEmail, setSendEmail] = useState(false);
  const { senderName, setSenderName } = useAtsSenderName(open);

  useEffect(() => {
    if (open) setSendEmail(!!autoSendDefault);
  }, [open, autoSendDefault]);

  const subjectPreview = resolveNonCandidate(rejectionTemplate?.subject, { cargo: positionName, empresa: orgName });
  const bodyPreview = resolveNonCandidate(rejectionTemplate?.body, { cargo: positionName, empresa: orgName });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('ats.detail.bulkDiscardTitle')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          {t('ats.detail.bulkDiscardConfirm', { count })}
        </Typography>

        <FormControlLabel
          sx={{ mt: 2 }}
          control={<Checkbox checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />}
          label={t('ats.detail.discardSendEmail')}
        />

        {sendEmail && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {t('ats.detail.bulkEmailPreviewNote')}
            </Typography>
            <TextField
              fullWidth
              size="small"
              label={t('ats.config.emailSender.fieldLabel')}
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              inputProps={{ maxLength: 80 }}
              sx={{ mb: 1.5 }}
            />
            <TextField
              fullWidth
              size="small"
              label={t('ats.detail.discardEmailSubject')}
              value={subjectPreview}
              InputProps={{ readOnly: true }}
              sx={{
                mb: 1.5,
                '& .MuiInputBase-root': { backgroundColor: '#F3F4F6', cursor: 'not-allowed' },
                '& .MuiInputBase-input, & textarea': { cursor: 'not-allowed' },
              }}
            />
            <TextField
              fullWidth
              multiline
              minRows={5}
              label={t('ats.detail.discardEmailBody')}
              value={bodyPreview}
              InputProps={{ readOnly: true }}
              sx={{
                '& .MuiInputBase-root': { backgroundColor: '#F3F4F6', cursor: 'not-allowed' },
                '& .MuiInputBase-input, & textarea': { cursor: 'not-allowed' },
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">{t('cancel')}</Button>
        <Button
          onClick={() => onConfirm && onConfirm({
            sendEmail,
            emailSubject: subjectPreview,
            emailBody: bodyPreview,
            senderName,
          })}
          color="error"
          variant="contained"
        >
          {t('ats.detail.discardConfirmButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AtsBulkDiscardDialog;
